/**
 * Y.Doc schema for lesson state synchronization.
 *
 * Document ID convention: `lesson-state:{lessonId}`
 *
 * The Y.Doc contains a single top-level Y.Map ('lesson') with flat primitive values.
 * Only properties listed in SyncedLessonState are synced via Yjs.
 */

/** Top-level Y.Map key in the Y.Doc */
export const LESSON_MAP_KEY = 'lesson';

/**
 * Y.Array keys for HTML-element DOM-mutation batches per slide layer (interactive iframe elements).
 * Each array item is a JSON-serialized DomMutation[] batch.
 * Cleared on slide change; tombstones are negligible with gc:true.
 *
 * Renamed from `mutations-layer-*` when drawing vacated these arrays for `drawing-objects-layer-*`
 * (fabric canvas migration). All three layers are retained: HTML-element/rubber slides reach layers
 * 2/3 via `element`/`forward`/`overlay` hotspots (see `audit-findings.md`).
 */
export const HTML_ELEMENT_MUTATION_ARRAY_KEYS = {
    1: 'html-element-mutations-1',
    2: 'html-element-mutations-2',
    3: 'html-element-mutations-3',
} as const satisfies Record<1 | 2 | 3, string>;

/**
 * Y.Array keys for per-layer dynamic content (text extensions, perception images,
 * bullet points). Each array item is a JSON-serialized ITransferDynamicContentSlide.
 *
 * Migrated from Y.Map JSON-string fields in 2026-05 to fix a bandwidth bug: the
 * old read-modify-write of a JSON-stringified array made every append's SignalR
 * payload grow linearly with the items already present (12+ kB per click after
 * a few appends). Y.Array.push() encodes each append as an O(1) delta regardless
 * of array length.
 *
 * Cleared on slide change (cascade in `setSlide`/`removeSlide`) and on
 * `resetSlideToInitial`. Tombstones negligible with `gc:true`.
 */
export const DYNAMIC_CONTENT_ARRAY_KEYS = {
    1: 'dynamic-content-layer-1',
    2: 'dynamic-content-layer-2',
    3: 'dynamic-content-layer-3',
} as const satisfies Record<1 | 2 | 3, string>;

/**
 * Y.Array keys for per-layer vector drawing objects (fabric.js canvas migration,
 * `migrate-slides-to-fabric-canvas`). Each array item is a JSON-serialized
 * {@link DrawingObject} (a stroke/shape/text primitive with a stable id and
 * logical-space coordinates). The canvas is a pure projection of these arrays:
 * adding/removing/clearing an entry reconciles the canvas with no imperative teardown.
 *
 * Distinct from {@link HTML_ELEMENT_MUTATION_ARRAY_KEYS} (DOM mutations for HTML-element slides,
 * retained on all three layers — see `audit-findings.md`). A given layer carries
 * EITHER drawing objects OR html-element mutations depending on the slide's type;
 * the two channels never overlap.
 *
 * Cleared on slide change (cascade in `setSlide`/`removeSlide`) and on
 * `resetSlideToInitial`. Tombstones negligible with `gc:true`.
 */
export const DRAWING_OBJECT_ARRAY_KEYS = {
    1: 'drawing-objects-layer-1',
    2: 'drawing-objects-layer-2',
    3: 'drawing-objects-layer-3',
} as const satisfies Record<1 | 2 | 3, string>;

/**
 * Per-layer Y.Map keys for the `FabricYjsBinding` metadata map. The binding requires
 * a metadata Y.Map (it stores a *synced* background URL there). Classroom does NOT
 * sync the background — each client resolves its own resolution variant locally via
 * `setBackgroundLocal` — so these maps stay empty in practice; they exist only to
 * satisfy the binding's constructor with a doc-attached map per layer.
 */
export const DRAWING_METADATA_MAP_KEYS = {
    1: 'drawing-meta-layer-1',
    2: 'drawing-meta-layer-2',
    3: 'drawing-meta-layer-3',
} as const satisfies Record<1 | 2 | 3, string>;

/**
 * Y.Array key for the append-only history of primary-layer slide changes during a lesson.
 * Sent to the server with the prepare-attendance-list REST request to compute time per lection.
 */
export const PRIMARY_SLIDE_HISTORY_KEY = 'primary-slide-history';

/**
 * One entry in the primary-slide history Y.Array, recorded whenever the teacher
 * navigates to a new slide on layer 1 during a running lesson.
 */
export interface PrimarySlideHistoryEntry {
    readonly moduleId: number;
    readonly versionId: number;
    /**
     * The lection this slide belongs to — the module structure's top-level node
     * (`parentId === null`), whatever that level is named in the module at hand:
     * the lection in a two-level module, the single level in a one-level module.
     *
     * `null` means "not attributable": the module slide (equivalent to showing no
     * slide), or a slide whose root ancestor could not be resolved. Such entries
     * still consume their time window server-side but are excluded from the
     * per-lection accumulation — which is why a null entry is strictly better than
     * a *missing* one, whose time would silently fall to the preceding entry.
     *
     * Never `0`: the server rejects `lectionId: 0` outright, deliberately, so the
     * "0 means several different things" defect class cannot recur here.
     *
     * Resolved by the teacher app at navigation time and passed in — `shared/classroom`
     * has no module-structure knowledge of its own.
     */
    readonly lectionId: number | null;
    /**
     * Diagnostic only — the server no longer resolves anything from it (lection
     * attribution comes from `lectionId`). Still required on the wire, and legally
     * `0` for client-synthesised virtual slides such as difficult-question slides.
     */
    readonly slideId: number;
    /** ISO-8601 UTC timestamp produced via `new Date().toISOString()` at the moment the slide was shown. */
    readonly shownAt: string;
}

/**
 * Lesson lifecycle status. Distinguishes a local-only "draft" Y.Doc (teacher
 * has a module open but no lesson started yet) from an actively running lesson.
 *
 * - `'draft'`  — local-only Y.Doc, no SignalR provider attached. Display reads
 *                from this Y.Doc just like a live lesson, so the rendering path
 *                is identical pre- and post-lesson-start.
 * - `'active'` — lesson is running and synced.
 *
 * **Phase 5e decision (2026-05-08): keep `paused` / `isLessonStopPending` as
 * separate booleans rather than subsuming them into `lessonStatus`.** The
 * `'paused'` / `'stopping'` enum values were originally added in Phase 1 with
 * the idea of unifying the state machine, but Phase 5 verified they were never
 * wired up — `pauseLesson()` / `beginLessonStop()` in the binding only flip
 * the booleans; `lessonStatus` stays `'active'` for the entire lesson. The
 * boolean fields remain the source of truth because:
 *   1. ~20 external consumers (selectors, components, effects across teacher,
 *      beamer, classroom-data-access) read `paused` / `isLessonStopPending`
 *      via `fromClassroom.isLessonPaused` and similar; subsuming would touch
 *      every site.
 *   2. The booleans are independent of the draft/active distinction — a draft
 *      Y.Doc is never paused or stopping, and these states are mutually
 *      orthogonal during a lesson (you can pause, then start stopping).
 *   3. The redundancy is small and harmless.
 *
 * The `'paused'` / `'stopping'` enum values are kept in the type for
 * forward-compatibility but are not currently produced by any writer.
 *
 * Naming note: avoids `'live'` to prevent confusion with the unrelated
 * LiveLesson video conferencing apps.
 */
export type LessonStatus = 'draft' | 'active' | 'paused' | 'stopping';

/**
 * Per-layer perception-image reveal display-state (canvas migration,
 * `canvas-image-slides`). Stored as a JSON string in a `<layer>SlideRevealState`
 * Y.Map field. Source of truth for whether a perception image is currently revealed.
 *
 * - `revealed` — pins the image + drawings visible (stops the hide animation). It is a
 *   per-SHOWING flag, NOT lesson-wide: it resets to `false` whenever the slide is shown
 *   (`setSlide`) or reset (`resetSlideToInitial`), so navigating away and back — or a
 *   same-slide content reset — re-hides and replays the animation, matching the SVG
 *   baseline (`svg-canvas.component` `startAnimation` / `revealPerceptionImage`). A
 *   late-joiner / refresh restores the current value (revealed stays revealed).
 * - `epoch` — a monotonic "show counter" bumped on `resetSlideToInitial`. It exists so a
 *   same-slide content reset produces a *changing* synced value even when `revealed` was
 *   already `false`; remote clients re-run the hide animation when the epoch changes. (A
 *   plain boolean reset to the same `false` would be memoized away and never reach the beamer.)
 */
export interface PerceptionRevealState {
    readonly epoch: number;
    readonly revealed: boolean;
}

/** Immutable default — frozen so it can be handed out as a shared parse fallback without risk of mutation. */
export const DEFAULT_PERCEPTION_REVEAL_STATE: PerceptionRevealState = Object.freeze({ epoch: 0, revealed: false });

/** Default `<layer>SlideRevealState` JSON string. Derived so it can never drift from the object. */
const DEFAULT_PERCEPTION_REVEAL_STATE_JSON = JSON.stringify(DEFAULT_PERCEPTION_REVEAL_STATE);

/**
 * Parse a `<layer>SlideRevealState` JSON string into a {@link PerceptionRevealState}, coercing
 * defensively (non-finite `epoch` → 0, `revealed` truthiness narrowed to a strict boolean) and
 * falling back to the default on null / parse error. The single parser for this field — used by
 * both the binding (write-side read-modify-write) and the facade (read-side Signal) so the two
 * paths can never disagree on what a malformed-but-parseable value means. `onError` is invoked
 * only on a genuine parse exception, letting the binding log Y.Doc corruption (the facade's
 * per-emission `computed` passes no handler to avoid log spam).
 */
export function parsePerceptionRevealState(
    raw: string | null | undefined,
    onError?: (err: unknown) => void,
): PerceptionRevealState {
    if (raw === null || raw === undefined) return DEFAULT_PERCEPTION_REVEAL_STATE;
    try {
        const parsed = JSON.parse(raw) as Partial<PerceptionRevealState>;
        return {
            epoch: typeof parsed.epoch === 'number' && Number.isFinite(parsed.epoch) ? parsed.epoch : 0,
            revealed: parsed.revealed === true,
        };
    } catch (err) {
        onError?.(err);
        return DEFAULT_PERCEPTION_REVEAL_STATE;
    }
}

/**
 * Per-layer synced hotspot-deactivation display-state (`canvas-renderer-composition` D6/C14). Stored
 * as a JSON string in a `<layer>SlideDeactivatedHotspots` Y.Map field: an array of the INDICES (into
 * the slide's filtered supported-hotspot list) that have been activated-and-consumed and must no
 * longer hit-test or highlight. Written by any Classroom writer (Teacher OR Beamer — `isWriter`),
 * lifted out of the renderer's former per-client local `HotspotLayer.deactivated` Set so a
 * non-`forward` hotspot activation now syncs to every client (Decision C14: state up, projected down).
 *
 * ⚠️ The index is only cross-client-stable while every client builds the SAME filtered list — i.e.
 * runs an identical `HotspotLayer.SUPPORTED_TYPES` (same code version). Changing that filter is a
 * breaking wire-format change; see the invariant note there. (No stable hotspot id exists to key on.)
 *
 * Per-SHOWING, not lesson-wide: resets to `[]` whenever the slide is shown (`setSlide`), removed
 * (`removeSlide`), or reset (`resetSlideToInitial`), so navigating away and back re-arms every hotspot.
 * No `epoch` (unlike {@link PerceptionRevealState}): valid ONLY while deactivation is a pure projection
 * (the renderer reads the set live for dim + hit-test) with no replay-on-re-arm effect — clearing a
 * non-empty set to `[]` is itself the change the projection reacts to, and an empty→empty reset is a
 * no-op with nothing to re-arm. If a future consumer needs a one-shot effect on re-arm, add an epoch
 * (as reveal-state has) so the already-empty reset still produces a changing synced value.
 */
export const DEFAULT_DEACTIVATED_HOTSPOTS: readonly number[] = Object.freeze([]);

/** Default `<layer>SlideDeactivatedHotspots` JSON string. Derived so it can never drift from the value. */
const DEFAULT_DEACTIVATED_HOTSPOTS_JSON = JSON.stringify(DEFAULT_DEACTIVATED_HOTSPOTS);

/**
 * Parse a `<layer>SlideDeactivatedHotspots` JSON string into a de-duplicated array of non-negative
 * integer indices, coercing defensively (non-array → `[]`; drops non-finite / negative / non-integer
 * entries) and falling back to `[]` on null / parse error. Single parser for this field — shared by
 * the binding (write-side read-modify-write) and the facade (read-side Signal) so they never disagree.
 */
export function parseDeactivatedHotspots(
    raw: string | null | undefined,
    onError?: (err: unknown) => void,
): number[] {
    if (raw === null || raw === undefined) return [];
    try {
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) return [];
        const seen = new Set<number>();
        for (const entry of parsed) {
            if (typeof entry === 'number' && Number.isInteger(entry) && entry >= 0) seen.add(entry);
        }
        return [...seen];
    } catch (err) {
        onError?.(err);
        return [];
    }
}

/**
 * Subset of ClassroomState that is synchronized via Yjs.
 * All values must be flat primitives (boolean, string, null) — no Date, no nested objects.
 */
export interface SyncedLessonState {
    // --- Lesson control ---
    // The lifecycle flags below (lessonStatus … isLessonStopPending) are LESSON-SCOPED:
    // they describe the lesson itself, not what is on screen, and must never cross a
    // document handoff. Each of them is a member of `LESSON_SCOPED_KEYS` — when adding
    // a new lifecycle flag here, add it there too, or it will silently leak across
    // lesson start/stop. `activeExtensionApp` sits in this block but is deliberately
    // NOT lesson-scoped — see its own comment.

    /** Lesson lifecycle status. See `LessonStatus` for semantics. Defaults to `'draft'`. */
    lessonStatus: LessonStatus;
    paused: boolean;
    pauseImageUrl: string | null;
    isShowingStudentRegistrationCode: boolean;
    /** True while the teacher is in the lesson-stop registration flow. Triggers the registration dialog on beamer. */
    isLessonStopPending: boolean;
    /**
     * Currently open extension app, or null if none is open. Synced via Yjs for late-join recovery.
     *
     * DISPLAY state, not lesson control, despite sitting in this block: the open app is
     * visible above the preview screen, and which app is open is a property of what the
     * teacher is showing. It is therefore NOT in `LESSON_SCOPED_KEYS` and is carried
     * across the lesson → draft handoff (stop). On the draft → lesson handoff (start) it
     * is deliberately dropped as an interim measure — the app's OWN Y.Doc does not hand
     * off, so a carried "open" would show an unsynced app; see the start-edge exception
     * in `LessonYjsService.initialize()` and OpenSpec change
     * `practice-trainer-state-takeover`.
     */
    activeExtensionApp: null | 'PushTheButton' | 'PracticeTrainer';

    // --- Phase 3: Slide state (flat primitives; slides serialized as JSON strings) ---
    // Dynamic content (text extensions, perception images, bullet points) lives in
    // sibling Y.Arrays keyed by `DYNAMIC_CONTENT_ARRAY_KEYS`, not in this Y.Map.
    primarySlide: string | null;                 // JSON: ITransferDisplaySlide | null
    primarySlideIsAccessedViaHotspot: boolean;
    primarySlideViewBox: string;                 // e.g. '0 0 1280 720'
    primarySlideTextboardPage: number;           // 1-based, default 1
    primarySlideRevealState: string;             // JSON: PerceptionRevealState
    primarySlideDeactivatedHotspots: string;     // JSON: number[] (deactivated hotspot indices)
    /**
     * Settled scroll-video frame index (`canvas-scroll-video-slides`), or `null` when the slide
     * has not been scrubbed (display then falls back to the slide's seed frame,
     * `ScrollVideo.current ?? start`). Written debounced when scrubbing settles — the LIVE scrub
     * stream rides the ephemeral awareness channel and never touches the Y.Doc. Resets to `null`
     * on show / remove / same-slide reset (per-showing, like the sibling display-state fields).
     */
    primarySlideScrollVideoFrame: number | null;
    /**
     * Identity of the current *showing* of this layer's slide (`slide-showing-identity`).
     *
     * A fresh UUID v4 is minted for every showing — `setSlide`,
     * `resetSlideToInitial` and `removeSlide` — inside the same transaction as the slide
     * identity write. State *within* one showing (viewBox, textboard page, scroll-video
     * frame, reveal, hotspot deactivation) does NOT mint a new id. Showing the same slide
     * twice therefore yields two different ids.
     *
     * Purpose: drawings carry a matching `showingId` stamp, so a drawing made on an earlier
     * showing cannot render on a later one even if it reaches a canvas by a path that
     * bypasses the `clearDrawingObjects` cascade (a concurrent writer whose insert survives
     * the clear, or an object untracked by the current binding).
     *
     * **A UUID, deliberately not a monotonic counter** — unlike
     * {@link PerceptionRevealState}'s `epoch`, which is only ever bumped by one writer's
     * reset. The case this field defends against is two *concurrent* writers, who would
     * compute the same next counter value from the same observed state and produce
     * colliding "different" showings that compare equal. A UUID needs no coordination.
     *
     * DISPLAY state: it must stay OUT of `LESSON_SCOPED_KEYS` so the handoff carries it in
     * the same snapshot as the stamped drawing objects — otherwise a fresh document keeps
     * the default while carried objects hold a real id, and every carried drawing is
     * rejected as stale.
     */
    primarySlideShowingId: string;

    secondarySlide: string | null;
    secondarySlideIsAccessedViaHotspot: boolean;
    secondarySlideViewBox: string;
    secondarySlideTextboardPage: number;
    secondarySlideRevealState: string;
    secondarySlideDeactivatedHotspots: string;
    secondarySlideScrollVideoFrame: number | null;
    /** Showing identity for layer 2 — see {@link SyncedLessonState.primarySlideShowingId}. */
    secondarySlideShowingId: string;

    tertiarySlide: string | null;
    tertiarySlideIsAccessedViaHotspot: boolean;
    tertiarySlideViewBox: string;
    tertiarySlideTextboardPage: number;
    tertiarySlideRevealState: string;
    tertiarySlideDeactivatedHotspots: string;
    tertiarySlideScrollVideoFrame: number | null;
    /** Showing identity for layer 3 — see {@link SyncedLessonState.primarySlideShowingId}. */
    tertiarySlideShowingId: string;

    // --- Phase 4: Navigation preview (flat JSON blobs; replaces SignalR UPDATE_PREVIEW_FOR_* commands) ---
    /**
     * JSON: ITransferMinimumSlideInformationList = { imageBaseUrl, sas, slides: ITransferMinimumSlideInformation[], hasParentSlide? }
     * Teacher-set; beamer uses to render thumbnail navigation and back-navigation button.
     */
    navigationPreviewBasicSlides: string;
    /**
     * JSON: { basicSlideIdx?: number, extensionSlideIdx?: number, extensionSlideList: ITransferMinimumSlideInformationList, assignedDriverscamVideo?: object } | null
     * Teacher-set; beamer uses to render next-slide / extension-slide thumbnail strip.
     */
    navigationPreviewNextSlides: string;
}

/** Default values for a fresh lesson Y.Doc. `lessonStatus` defaults to `'draft'` —
 *  live-lesson initialization explicitly upgrades it to `'active'` after writing defaults. */
export const SYNCED_LESSON_STATE_DEFAULTS: SyncedLessonState = {
    lessonStatus: 'draft',
    paused: false,
    pauseImageUrl: null,
    isShowingStudentRegistrationCode: false,
    isLessonStopPending: false,
    activeExtensionApp: null,

    primarySlide: null,
    primarySlideIsAccessedViaHotspot: false,
    primarySlideViewBox: '0 0 1280 720',
    primarySlideTextboardPage: 1,
    primarySlideRevealState: DEFAULT_PERCEPTION_REVEAL_STATE_JSON,
    primarySlideDeactivatedHotspots: DEFAULT_DEACTIVATED_HOTSPOTS_JSON,
    primarySlideScrollVideoFrame: null,
    primarySlideShowingId: '',

    secondarySlide: null,
    secondarySlideIsAccessedViaHotspot: false,
    secondarySlideViewBox: '0 0 1280 720',
    secondarySlideTextboardPage: 1,
    secondarySlideRevealState: DEFAULT_PERCEPTION_REVEAL_STATE_JSON,
    secondarySlideDeactivatedHotspots: DEFAULT_DEACTIVATED_HOTSPOTS_JSON,
    secondarySlideScrollVideoFrame: null,
    secondarySlideShowingId: '',

    tertiarySlide: null,
    tertiarySlideIsAccessedViaHotspot: false,
    tertiarySlideViewBox: '0 0 1280 720',
    tertiarySlideTextboardPage: 1,
    tertiarySlideRevealState: DEFAULT_PERCEPTION_REVEAL_STATE_JSON,
    tertiarySlideDeactivatedHotspots: DEFAULT_DEACTIVATED_HOTSPOTS_JSON,
    tertiarySlideScrollVideoFrame: null,
    tertiarySlideShowingId: '',

    navigationPreviewBasicSlides: '{"slides":[],"hasParentSlide":false}',
    navigationPreviewNextSlides: 'null',
};

/** All valid keys in SyncedLessonState, used for filtering Y.Map observer events */
export const SYNCED_LESSON_STATE_KEYS: ReadonlySet<string> = new Set<string>(
    Object.keys(SYNCED_LESSON_STATE_DEFAULTS)
);

/**
 * The closed set of Y.Map keys that must NOT cross a document handoff
 * (draft → lesson on start, lesson → draft on stop).
 *
 * The cut is "describes the lesson" vs. "describes what is on screen":
 * lifecycle flags and lesson-level overlays are lesson-scoped and reset with
 * every new document; everything else in the schema is display state and is
 * carried automatically by `LessonYjsBinding.exportDisplayState()` /
 * `importDisplayState()` (which iterate `SYNCED_LESSON_STATE_KEYS` minus this
 * set). Deliberately NOT in this set, despite the schema's lesson-control block
 * grouping: `activeExtensionApp` (visible above the preview screen — a property
 * of what the teacher is showing, not of the lesson's lifecycle).
 *
 * `primary-slide-history` needs no entry here: it lives in a sibling Y.Array
 * that the export simply never copies — it dies with the source document.
 *
 * ⚠️ When adding a new lifecycle flag to `SyncedLessonState`, add it here too.
 * A forgotten flag is carried across handoffs by default — e.g. a lesson's
 * `paused: true` would leak into the post-stop draft and paint a pause image
 * over the workspace. The partition test in `lesson-yjs-binding.spec.ts`
 * (`LESSON_SCOPED_KEYS ⊎ display keys === SYNCED_LESSON_STATE_KEYS`) keeps this
 * set and the schema honest.
 */
export const LESSON_SCOPED_KEYS: ReadonlySet<string> = new Set<string>([
    'lessonStatus',
    'paused',
    'pauseImageUrl',
    'isShowingStudentRegistrationCode',
    'isLessonStopPending',
]);

/**
 * Display state carried across a Classroom Y.Doc handoff (lesson start/stop) —
 * the return type of `LessonYjsBinding.exportDisplayState()` and the input of
 * `importDisplayState()`.
 *
 * - `lessonMap` — the display subset of the `lesson` Y.Map: every
 *   `SYNCED_LESSON_STATE_KEYS` member not in {@link LESSON_SCOPED_KEYS}.
 *   Plain JSON values, re-written as fresh local writes on import (never
 *   `Y.encodeStateAsUpdate` — that would import the source document's client
 *   ids/clock entries into the fresh document).
 * - `dynamicContent` / `htmlElementMutations` — per-layer JSON-string arrays,
 *   copied verbatim.
 * - `drawingObjects` — per-layer drawing objects as plain JSON
 *   (`Y.Map.toJSON()`), reconstructed as fresh `Y.Map`s on import.
 * - `drawingMetadata` — per-layer drawing metadata maps as plain JSON.
 *
 * The `primary-slide-history` Y.Array is deliberately absent: history is
 * lesson-scoped (it feeds the attendance list's time-per-lection maths) and
 * dies with the source document.
 */
export interface DisplayStateSnapshot {
    lessonMap: Record<string, unknown>;
    dynamicContent: Record<1 | 2 | 3, string[]>;
    htmlElementMutations: Record<1 | 2 | 3, string[]>;
    drawingObjects: Record<1 | 2 | 3, Record<string, unknown>[]>;
    drawingMetadata: Record<1 | 2 | 3, Record<string, unknown>>;
}
